/* =============================================================================
   ClearUp — runtime
   =============================================================================
   Loaded ONLY from layout/clearup.liquid. Vanilla ES2020, zero dependencies.
   No jQuery: the legacy layout pulls jQuery 3.5.1 from a CDN render-blocking,
   and nothing in this layer may depend on it.

   WHY THIS FILE EXISTS AT ALL
   Pipeline's own section registry (`register()` at assets/ds-theme.dev.js:1184) is
   module-scoped inside that file's IIFE and is therefore unreachable from here.
   More decisively, assets/ds-theme.js is not loaded on the clearup layout, so
   `window.Shopify.theme.sections.registered` is empty and its `load('*')` never
   runs. This is a small reimplementation of the SAME lifecycle contract, so the
   idiom stays familiar to anyone who has read theme.dev.js.

   For the same reason, none of the `theme:*` events documented in
   assets/ds-custom.js fire on clearup pages. We re-emit them with identical names
   and `detail` shapes so ds-custom.js and any app/merchant listeners keep working
   across a mixed legacy/clearup session.

   WHEN TO USE WHICH
     Custom element  — anything that owns its own state, or that lives inside a
                       region replaced via the Section Rendering API. Custom
                       elements self-upgrade on insertion, which removes the
                       manual re-bind dance at ds-theme.dev.js:4880.
                       e.g. <cu-qty>, <cu-accordion>, <cu-gallery>, <cu-cart>
     Registry module — section-level orchestration that owns the whole container
                       and is never innerHTML'd: sticky-bar show/hide, scroll
                       behaviour, Theme Editor onSelect.
   ========================================================================== */

(function () {
  'use strict';

  var CU = (window.CU = window.CU || {});

  /* ================ Section registry =================================== */

  var selectors = {
    section: '[data-section-type]',
    id: 'data-section-id',
    type: 'data-section-type',
  };

  var registered = {};
  var instances = [];

  /**
   * Register behaviour for a section type.
   * @param {string} type  matches [data-section-type]
   * @param {object|object[]} components  each may implement onLoad, onUnload,
   *   onSelect, onDeselect, onBlockSelect, onBlockDeselect. Every hook is
   *   invoked with `this` = { id, type, container }.
   */
  function register(type, components) {
    if (!registered[type]) registered[type] = [];
    [].concat(components).forEach(function (component) {
      registered[type].push(component);
    });
  }

  function Section(container) {
    this.container = container;
    this.id = container.getAttribute(selectors.id);
    this.type = container.getAttribute(selectors.type);
    this.components = (registered[this.type] || []).slice();
    this.callHook('onLoad');
  }

  Section.prototype.callHook = function (hook, event) {
    var self = this;
    this.components.forEach(function (component) {
      if (typeof component[hook] === 'function') {
        component[hook].call(self, event);
      }
    });
  };

  Section.prototype.unload = function () {
    this.callHook('onUnload');
  };

  function load(target) {
    var containers =
      target === '*' || target == null
        ? document.querySelectorAll(selectors.section)
        : [target];

    [].forEach.call(containers, function (container) {
      if (!container.hasAttribute || !container.hasAttribute(selectors.type)) return;
      if (instanceFor(container)) return; // already loaded
      instances.push(new Section(container));
    });
  }

  function unload(container) {
    instances = instances.filter(function (instance) {
      if (instance.container !== container) return true;
      instance.unload();
      return false;
    });
  }

  function instanceFor(container) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].container === container) return instances[i];
    }
    return null;
  }

  function instanceForId(id) {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i].id === id) return instances[i];
    }
    return null;
  }

  CU.section = { register: register, load: load, unload: unload };

  /* ================ Theme Editor ======================================= */

  function bindDesignMode() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    document.addEventListener('shopify:section:load', function (event) {
      var container = event.target.querySelector(selectors.section) || event.target;
      load(container);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var container = event.target.querySelector(selectors.section) || event.target;
      unload(container);
    });

    ['select', 'deselect'].forEach(function (name) {
      document.addEventListener('shopify:section:' + name, function (event) {
        var instance = instanceForId(event.detail.sectionId);
        if (instance) instance.callHook(name === 'select' ? 'onSelect' : 'onDeselect', event);
      });
    });

    ['select', 'deselect'].forEach(function (name) {
      document.addEventListener('shopify:block:' + name, function (event) {
        var instance = instanceForId(event.detail.sectionId);
        if (instance) {
          instance.callHook(name === 'select' ? 'onBlockSelect' : 'onBlockDeselect', event);
        }
      });
    });
  }

  /* ================ Event bus ==========================================
     Same names and detail shapes as the legacy bus documented in
     assets/ds-custom.js, so listeners written against Pipeline keep working.
     ==================================================================== */

  function emit(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail: detail })
    );
  }

  CU.emit = emit;

  function bindScroll() {
    var last = window.pageYOffset;
    var ticking = false;

    function report() {
      var y = window.pageYOffset;
      emit('theme:scroll', { down: y > last, position: y });
      if (y > last) emit('theme:scroll:down', { position: y });
      else if (y < last) emit('theme:scroll:up', { position: y });
      last = y;
      ticking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(report);
      },
      { passive: true }
    );
  }

  function bindResize() {
    var timer;
    window.addEventListener(
      'resize',
      function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          emit('theme:resize', { width: window.innerWidth, height: window.innerHeight });
        }, 150);
      },
      { passive: true }
    );
  }

  /* ================ Scroll lock ========================================
     ds-theme.js ships bodyScrollLock from ds-vendor.js; we don't load either. This
     is a minimal equivalent honouring the same contract as ds-custom.js:62 —
     `theme:scroll:lock` carries the scrollable inner element in `detail` so
     the locked overlay can still scroll internally.
     ==================================================================== */

  var lockState = { count: 0, y: 0 };

  function lockScroll() {
    if (lockState.count++ > 0) return;

    lockState.y = window.pageYOffset;
    var scrollbar = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.position = 'fixed';
    document.body.style.top = -lockState.y + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    if (scrollbar > 0) document.body.style.paddingRight = scrollbar + 'px';
    document.documentElement.style.setProperty('--cu-scrollbar', scrollbar + 'px');
  }

  function unlockScroll() {
    if (lockState.count === 0) return;
    if (--lockState.count > 0) return;

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.paddingRight = '';
    window.scrollTo(0, lockState.y);
  }

  CU.lockScroll = lockScroll;
  CU.unlockScroll = unlockScroll;

  document.addEventListener('theme:scroll:lock', lockScroll);
  document.addEventListener('theme:scroll:unlock', unlockScroll);

  /* ================ Cart drawer (Phase 0 stub) =========================
     Open/close, scrim, scroll lock, bag count. The engine — four states,
     thresholds, progress meter, discount codes — lands in a later phase; see
     the notes in sections/cu-cart-drawer.liquid.

     This is a registry module rather than a custom element because it owns the
     whole section container and is never innerHTML'd. The regions INSIDE it
     that do get replaced via the Section Rendering API will be custom elements.
     ==================================================================== */

  /* ================ 18. Cart slide-over ================================
     Open/close, and every mutation the drawer can make.

     THE ONE ARCHITECTURAL RULE HERE: this module never templates cart markup.
     compare_at_price is absent from the line objects /cart/*.js returns, and
     the design strikes a "was" figure on every line, so the panel is rendered
     by Liquid and re-rendered server-side after each change. Shopify's cart
     endpoints take a `sections` parameter and return the rendered section
     alongside the new cart, so that costs one round trip rather than two.

     Only [data-cu-cart-content] is swapped. The root, the scrim and the panel
     survive, which is what keeps the drawer open — and its transform
     un-restarted — across a mutation. Everything inside the swapped region is
     therefore bound by DELEGATION from the root; nothing below attaches a
     listener to a button.
     ==================================================================== */

  var cartSelectors = {
    drawer: '#cu-cart',
    toggle: '[data-cu-cart-toggle]',
    close: '[data-cu-cart-close]',
    scroll: '[data-cu-cart-scroll]',
    count: '[data-cu-cart-count]',
    content: '[data-cu-cart-content]',
    line: '[data-cu-cart-key]',
    remove: '[data-cu-cart-remove]',
    qty: '[data-cu-cart-qty]',
    upsell: '[data-cu-cart-upsell]',
    codeInput: '[data-cu-cart-code-input]',
    codeApply: '[data-cu-cart-code-apply]',
    codeRemove: '[data-cu-cart-code-remove]',
    codeError: '[data-cu-cart-code-error]',
    code: '[data-cu-cart-code]',
  };

  var CART_SECTION = 'cu-cart-drawer';

  var cartDrawer = {
    onLoad: function () {
      var drawer = this.container;
      var self = this;
      var adding = false;
      var addConfirmed = false;
      var busyDepth = 0;
      var initialCartWork = null;
      var addTrigger = null;

      /* --- Open / close ------------------------------------------------ */

      this.cuOpen = function () {
        if (drawer.getAttribute('aria-hidden') === 'false') return;
        drawer.setAttribute('aria-hidden', 'false');
        emit('theme:scroll:lock', drawer.querySelector(cartSelectors.scroll));
        setToggleState(true);
      };

      this.cuClose = function () {
        if (drawer.getAttribute('aria-hidden') === 'true') return;
        drawer.setAttribute('aria-hidden', 'true');
        emit('theme:scroll:unlock');
        setToggleState(false);
      };

      this.cuKeydown = function (event) {
        if (event.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
          self.cuClose();
        }
      };

      function setToggleState(open) {
        document.querySelectorAll(cartSelectors.toggle).forEach(function (el) {
          el.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }

      function setAddLoading(active) {
        var showItems = active && addConfirmed;
        drawer.toggleAttribute('data-cu-cart-adding', active);
        drawer.toggleAttribute('data-cu-cart-confirmed', showItems);
        drawer.querySelector('[data-cu-cart-loading]').hidden = !active || showItems;
        var inner = content();
        if (inner) {
          inner.inert = active && !showItems;
          if (active && !showItems) inner.setAttribute('aria-busy', 'true');
          else inner.removeAttribute('aria-busy');
          inner.querySelectorAll('.cu-cart__body, .cu-cart__foot, .cu-cart__result').forEach(function (part) { part.inert = active; });
          var status = inner.querySelector('[data-cu-cart-updating]');
          if (status) status.hidden = !showItems;
          var checkout = inner.querySelector('[name="checkout"]');
          if (checkout) {
            if (active) {
              if (!checkout.hasAttribute('data-cu-was-disabled')) checkout.setAttribute('data-cu-was-disabled', String(checkout.disabled));
              checkout.disabled = true;
            } else if (checkout.hasAttribute('data-cu-was-disabled')) {
              checkout.disabled = checkout.getAttribute('data-cu-was-disabled') === 'true';
              checkout.removeAttribute('data-cu-was-disabled');
            }
          }
        }
      }

      CU.beginCartAdd = function (message) {
        if (adding) { self.cuOpen(); return false; }
        if (busyDepth > 0 && !initialCartWork) return false;
        adding = true;
        addConfirmed = false;
        var loadingMessage = drawer.querySelector('[data-cu-cart-loading-message]');
        if (loadingMessage) loadingMessage.textContent = message || 'Adding to your bag…';
        addTrigger = document.activeElement;
        setAddLoading(true);
        self.cuOpen();
        drawer.querySelector('[data-cu-cart-loading] button').focus({ preventScroll: true });
        return true;
      };

      CU.finishCartAdd = function (succeeded) {
        adding = false;
        addConfirmed = false;
        setAddLoading(false);
        if (!succeeded) {
          var wasOpen = drawer.getAttribute('aria-hidden') === 'false';
          self.cuClose();
          if (wasOpen && addTrigger && addTrigger.isConnected) addTrigger.focus({ preventScroll: true });
        } else if (drawer.getAttribute('aria-hidden') === 'false') {
          content().querySelector('[data-cu-cart-close]').focus({ preventScroll: true });
        }
        addTrigger = null;
        publishCart();
      };

      function onToggleClick(event) {
        var toggle = event.target.closest(cartSelectors.toggle);
        if (!toggle) return;
        event.preventDefault();
        self.cuOpen();
        if (!adding && !busyDepth) CU.refreshCart().catch(function (error) { offerError(error.message); });
      }

      document.addEventListener('click', onToggleClick);
      document.addEventListener('keydown', this.cuKeydown);
      this.cuToggleHandler = onToggleClick;

      /* --- Rendering --------------------------------------------------- */

      function content() {
        return drawer.querySelector(cartSelectors.content);
      }

      /* Swap the rendered section in, keeping the shopper where they were.
         The scroll position is restored because a qty change three lines down
         a long cart otherwise jumps the panel back to the top. */
      function paint(html) {
        var target = content();
        if (!target || !html) return false;

        var scroller = drawer.querySelector(cartSelectors.scroll);
        var offset = scroller ? scroller.scrollTop : 0;

        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var next = parsed.querySelector(cartSelectors.content);
        if (!next) return false;

        target.replaceWith(next);
        if (adding) setAddLoading(true);

        var restored = drawer.querySelector(cartSelectors.scroll);
        if (restored) restored.scrollTop = offset;
        return true;
      }

      /* The bag count lives in the header, outside this section, so it is
         updated from the cart object rather than from the swapped markup. */
      function paintCount(cart) {
        if (!cart || typeof cart.item_count !== 'number') return;
        if (Array.isArray(cart.discount_codes)) CU.latestAjaxCart = cart;
        document.querySelectorAll(cartSelectors.count).forEach(function (el) {
          el.textContent = cart.item_count;
        });
        if (!adding && !busyDepth) emit('theme:cart:change', { cart: cart });
      }

      function publishCart() {
        if (!adding && !busyDepth) {
          var current = snapshot();
          if (CU.latestAjaxCart && window.CUOfferPolicy?.cartFingerprint(current) === window.CUOfferPolicy.cartFingerprint(CU.latestAjaxCart)) {
            current.discount_codes = CU.latestAjaxCart.discount_codes;
          }
          paintCount(current);
        }
      }

      function busy(state) {
        busyDepth = Math.max(0, busyDepth + (state ? 1 : -1));
        drawer.toggleAttribute('data-cu-cart-busy', busyDepth > 0);
        if (!busyDepth) publishCart();
      }

      function snapshot() {
        try { return JSON.parse(content().getAttribute('data-cu-cart-state')); }
        catch (error) { return { items: [], attributes: {} }; }
      }

      function offerSetting(name) { return content().getAttribute('data-cu-cart-' + name) || ''; }
      function usesDiscountPlus() { return offerSetting('discount-plus') === 'true'; }
      CU.usesDiscountPlus = usesDiscountPlus;
      function usesRebuyOffers() { return !usesDiscountPlus() && offerSetting('rebuy') === 'true'; }
      function isGift(item) {
        var properties = item.properties || {};
        return properties._cu_gift === 'true' || (usesDiscountPlus() && String(item.variant_id) === offerSetting('gift-variant')) || (offerSetting('rebuy-tier') && properties._tier === offerSetting('rebuy-tier'));
      }
      function offerItems(items) {
        if (!usesRebuyOffers()) return items;
        var variants = offerSetting('volume-variants').split(',');
        return items.map(function (item) {
          if (isGift(item) || variants.indexOf(String(item.variant_id || item.id)) === -1) return item;
          return Object.assign({}, item, { properties: Object.assign({}, item.properties, {
            _source: 'Rebuy', _attribution: 'Rebuy Buy More Save More',
          }) });
        });
      }

      // Reuse the installed server-side discount function without loading its
      // storefront SDK. These IDs must match the merchant's existing rules.
      var offerSetup;
      var discountWork, pendingProductOffer = '';
      function discountConfig() {
        var profile = offerSetting('discount-profile');
        return { rules: JSON.parse(content().querySelector('[data-cu-discount-rules]').textContent),
          giftId: offerSetting('gift-variant'), legacyTier: offerSetting('rebuy-tier'), profile: profile, offerPrefix: profile ? profile + ':' : '' };
      }
      function discountRequest(url, body) {
        if (body) body = Object.assign({}, body, { sections: CART_SECTION, sections_url: window.location.pathname });
        return fetch(url, body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { cache: 'no-store', headers: { Accept: 'application/json' } })
          .then(function (response) { return response.json().then(function (data) {
            if (!response.ok) throw new Error(data.description || 'Your discounts could not update. Please try again.');
            return data;
          }); });
      }
      function reconcileDiscounts(freshCart) {
        if (!usesDiscountPlus()) return Promise.resolve();
        if (discountWork) return discountWork;
        if (!window.CUDiscountEngine) return Promise.reject(new Error('Your discounts could not load. Please reload before checking out.'));
        if (!discountConfig().profile) return Promise.reject(new Error('Your bag offers are not configured. Please contact us.'));
        discountWork = window.CUDiscountEngine.reconcile({
          read: function () { return discountRequest(CU.routes.root + 'cart.js'); },
          update: function (body) { return discountRequest(CU.routes.cartUpdate, body); },
          change: function (body) { return discountRequest(CU.routes.cartChange, body); }
        }, discountConfig(), pendingProductOffer, freshCart).then(function (result) {
          CU.latestAjaxCart = result.cart;
          if (!result.changed) return result.cart;
          if (paint(result.cart.sections && result.cart.sections[CART_SECTION])) { paintCount(result.cart); return result.cart; }
          return discountRequest(CU.routes.cartUpdate, { sections: CART_SECTION, sections_url: window.location.pathname }).then(function (cart) {
            if (!paint(cart.sections && cart.sections[CART_SECTION])) throw new Error('Your bag could not refresh. Please reload before checking out.');
            paintCount(cart); return cart;
          });
        }).finally(function () { discountWork = null; });
        return discountWork;
      }
      CU.clearPendingProductOffer = function () { pendingProductOffer = ''; };
      function ensureOffers() {
        if (usesDiscountPlus()) return reconcileDiscounts();
        if (!usesRebuyOffers()) return Promise.resolve();
        if (offerSetup) return offerSetup;
        var attributes = { _source: 'Rebuy', _attribution: 'Smart Cart 2.0',
          _rebuyCartId: offerSetting('rebuy-id'), _barId: offerSetting('rebuy-bar') };
        if (!attributes._rebuyCartId || !attributes._barId || !offerSetting('rebuy-tier')) {
          return Promise.reject(new Error('The bag offers are not configured. Please contact us.'));
        }
        var current = snapshot().attributes || {};
        if (Object.keys(attributes).every(function (key) { return current[key] === attributes[key]; })) return Promise.resolve();
        offerSetup = fetch(CU.routes.cartUpdate, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attributes, sections: CART_SECTION, sections_url: window.location.pathname }),
        }).then(function (response) {
          if (!response.ok) throw new Error('Your bag could not be prepared. Please try again.');
          return response.json();
        }).then(function (cart) {
          paint(cart.sections && cart.sections[CART_SECTION]);
          paintCount(cart);
        }).finally(function () { offerSetup = null; });
        // Cache only in-flight setup. Cart clears or other integrations may
        // remove attribution; a previously completed request proves nothing
        // about the current cart's gift configuration.
        return offerSetup;
      }

      CU.prepareCartAdd = function (items) {
        return ensureOffers().then(function () { return offerItems(items); });
      };
      // Accept the first click during background initialization, then wait for
      // its reconciliation before sending the add. Internal recovery calls to
      // prepareCartAdd must not wait on the initialization they are completing.
      CU.waitForCartReady = function () { return initialCartWork || Promise.resolve(); };

      function offerError(message) {
        var slot = drawer.querySelector('[data-cu-cart-offer-error]');
        if (slot) { slot.textContent = message; slot.hidden = !message; }
      }
      CU.cartOfferError = offerError;

      /* Every mutation goes through here: one POST, one repaint, one gift
         reconciliation. Nothing else in this file calls the cart endpoints. */
      function mutate(url, body, offersReady) {
        busy(true);
        body.sections = CART_SECTION;
        body.sections_url = window.location.pathname;

        // Gift reconciliation just checked Discount Plus eligibility. Do not
        // repeat that preflight read before its immediately following write.
        return (offersReady && usesDiscountPlus() ? Promise.resolve() : ensureOffers()).then(function () {
          if (body.items) body.items = offerItems(body.items);
          if (url === CU.routes.cartChange && body.quantity > 0) {
            var line = snapshot().items.find(function (item) { return item.key === body.id || String(item.variant_id) === String(body.id); });
            if (line) body.properties = offerItems([line])[0].properties;
          }
          return fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
          });
        })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw new Error(data.description || 'Your bag could not be updated.');
              return data;
            });
          })
          .then(function (data) {
            /* /cart/add.js returns the LINE, not the cart, so its item_count
               has to come from the sections payload's sibling rather than
               from `data` itself. update/change return the cart. */
            if (!paint(data.sections && data.sections[CART_SECTION])) {
              return CU.refreshCart().then(function () { return data; });
            }
            if (typeof data.item_count === 'number') paintCount(data);
            else paintCount(snapshot());
            return data;
          })
          .catch(function (error) {
            /* A failed mutation must not leave a stale panel claiming the
               change worked. Re-render from the server and let the shopper
               see the truth. */
            return CU.refreshCart().then(function () { throw error; });
          })
          .then(function (result) {
            return reconcileGift(result).then(function () { return result; });
          }).finally(function () { busy(false); });
      }

      /* --- Free gift ---------------------------------------------------
         The thresholds are decided in Liquid and read off the wrapper; this
         never recomputes one, so the panel and the mechanism cannot disagree.

         `guard` is not paranoia. Adding the gift changes the cart, which
         re-renders the panel, which is exactly the state this function
         inspects — without it, a gift whose discount is misconfigured would
         raise the qualifying total and loop. Reconciliation runs once per
         mutation and never triggers itself. */
      var reconciling = false;
      var giftDeclined = false;
      try { giftDeclined = sessionStorage.getItem('cu-gift-declined') === 'true'; } catch (error) { /* Storage is optional. */ }

      function declineGift(declined) {
        giftDeclined = declined;
        try { sessionStorage.setItem('cu-gift-declined', String(declined)); } catch (error) { /* Storage is optional. */ }
      }

      function reconcileGift(freshCart) {
        return reconcileDiscounts(freshCart).then(reconcileGiftItems);
      }
      function reconcileGiftItems() {
        var el = content();
        if (!el || reconciling) return Promise.resolve();

        var variant = el.getAttribute('data-cu-cart-gift-variant');
        if (!variant) return Promise.resolve();

        var threshold = parseInt(el.getAttribute('data-cu-cart-gift-threshold'), 10);
        var qualifying = parseInt(el.getAttribute('data-cu-cart-qualifying'), 10);
        if (isNaN(threshold) || isNaN(qualifying)) return Promise.resolve();
        if (qualifying < threshold) declineGift(false);
        var wants = qualifying >= threshold && offerSetting('gift-available') === 'true' && !giftDeclined;
        var gifts = snapshot().items.filter(isGift);
        var remove = gifts.filter(function (gift, index) { return !wants || index > 0 || String(gift.variant_id) !== variant; });
        var keep = gifts.find(function (gift) { return remove.indexOf(gift) === -1; });
        if (!remove.length && (!wants || (keep && keep.quantity === 1 && keep.final_line_price === 0))) return Promise.resolve();
        reconciling = true;
        var work = Promise.resolve();
        remove.forEach(function (gift) {
          work = work.then(function () { return mutate(CU.routes.cartChange, { id: gift.key, quantity: 0 }, true); });
        });
        work = work.then(function () {
          if (!wants) return;
          if (keep) {
            if (keep.quantity !== 1) return mutate(CU.routes.cartChange, { id: keep.key, quantity: 1 }, true);
            return;
          }
          var properties = { _cu_gift: 'true' };
          if (usesRebuyOffers()) Object.assign(properties, {
            _source: 'Rebuy', _attribution: 'Rebuy Tiered Progress Bar', _tier: offerSetting('rebuy-tier'),
          });
          return mutate(CU.routes.cartAdd, { items: [{ id: variant, quantity: 1, properties: properties }] }, true);
        }).then(function () {
          var charged = snapshot().items.find(function (item) { return isGift(item) && item.final_line_price > 0; });
          if (charged) return mutate(CU.routes.cartChange, { id: charged.key, quantity: 0 }, true).then(function () {
            throw new Error('The gift could not be added for free. Please contact us.');
          });
        });
        return work.catch(function (error) {
          offerError(error.message || 'Your gift could not be updated. Please try again.');
          if (usesDiscountPlus()) throw error;
        })
          .finally(function () { reconciling = false; });
      }

      /* --- Delegated controls ------------------------------------------ */

      function keyFor(el) {
        var line = el.closest(cartSelectors.line);
        return line ? line.getAttribute('data-cu-cart-key') : null;
      }

      /* The full code list, rebuilt from the markup. The `discount` parameter
         REPLACES the whole set, so posting a new code alone silently drops
         every code already on the cart. */
      function codes(add, drop) {
        var list = [];
        drawer.querySelectorAll(cartSelectors.code).forEach(function (el) {
          var code = el.getAttribute('data-cu-cart-code');
          if (code && code !== drop) list.push(code);
        });
        if (add && list.indexOf(add) === -1) list.push(add);
        return list.join(',');
      }

      // Scratch rewards use the same cart repaint and offer reconciliation as
      // drawer actions. Read Ajax codes so vouchers saved on empty carts survive.
      CU.applyVoucher = function (code) {
        code = String(code || '').trim();
        if (!code || code.includes(',')) return Promise.reject(new Error('This voucher could not be added.'));
        return CU.waitForCartReady().then(function () {
          if (adding || busyDepth > 0) throw new Error('Your cart is updating. Please try again in a moment.');
          busy(true);
          function readCart() {
            return fetch(CU.routes.root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
              .then(function (response) {
                if (!response.ok) throw new Error('Your voucher could not be confirmed. Please try again.');
                return response.json();
              });
          }
          return readCart().then(function (cart) {
            if (!Array.isArray(cart.discount_codes)) throw new Error('Your vouchers could not load. Please try again.');
            var current = cart.discount_codes.map(function (entry) { return entry.code; });
            if (!current.some(function (entry) { return entry.toUpperCase() === code.toUpperCase(); })) current.push(code);
            return mutate(CU.routes.cartUpdate, { discount: current.join(',') });
          }).then(readCart).then(function (cart) {
            paintCount(cart);
            return cart;
          }).finally(function () { busy(false); });
        });
      };

      // Apply a verified pair code before adding its products. Shopify retains
      // currently ineligible codes and validates the exact pair on every update,
      // including checkout. Preserve the shopper's scratch/shipping codes.
      CU.prepareProductOffer = function (code) {
        if (!/^CU-PAIR-[A-Z]{2}-[135]-(10|15)$/.test(code)) return Promise.reject(new Error('This product offer is unavailable.'));
        if (usesDiscountPlus()) {
          if (!discountConfig().rules.some(function (r) { return r.code === code; })) return Promise.reject(new Error('This product offer is unavailable.'));
          pendingProductOffer = code;
          return Promise.resolve();
        }
        // Liquid's cart JSON omits discount_codes, including a scratch code
        // waiting for items in an empty cart. Read Ajax's complete code list.
        return fetch(CU.routes.root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
          .then(function (response) {
            if (!response.ok) throw new Error('Your offer could not be prepared. Please try again.');
            return response.json();
          }).then(function (cart) {
            var current = (cart.discount_codes || []).map(function (entry) { return entry.code; });
            if (!current.some(function (entry) { return entry.toUpperCase() === code; })) current.push(code);
            return mutate(CU.routes.cartUpdate, { discount: current.join(',') });
          }).then(function (cart) {
          if (!(cart.discount_codes || []).some(function (entry) { return entry.code.toUpperCase() === code; })) {
            throw new Error('The offer could not be applied. Please check the discounts in your cart and try again.');
          }
        });
      };

      function onDrawerClick(event) {
        var target = event.target;

        if (target.closest(cartSelectors.close)) {
          self.cuClose();
          return;
        }
        if (adding || busyDepth > 0) return;

        var tier = target.closest('[data-cu-cart-tier]');
        if (tier) {
          if (adding || drawer.hasAttribute('data-cu-cart-busy')) return;
          var tierKey = keyFor(tier);
          var tierQuantity = Number(tier.getAttribute('data-cu-cart-tier'));
          var tierLine = snapshot().items.find(function (item) { return item.key === tierKey; });
          if (!tierLine || isGift(tierLine) || (tierQuantity !== 2 && tierQuantity !== 3) || tierLine.quantity === tierQuantity) return;
          tier.disabled = true;
          tier.setAttribute('aria-busy', 'true');
          mutate(CU.routes.cartChange, { id: tierKey, quantity: tierQuantity })
            .catch(function (error) { offerError(error.message); })
            .finally(function () { tier.disabled = false; tier.removeAttribute('aria-busy'); });
          return;
        }

        var remove = target.closest(cartSelectors.remove);
        if (remove) {
          var removeKey = keyFor(remove);
          if (snapshot().items.some(function (item) { return item.key === removeKey && isGift(item); })) declineGift(true);
          if (removeKey) mutate(CU.routes.cartChange, { id: removeKey, quantity: 0 }).catch(function (error) { offerError(error.message); });
          return;
        }

        var upsell = target.closest(cartSelectors.upsell);
        if (upsell) {
          mutate(CU.routes.cartAdd, { items: [{ id: upsell.value, quantity: 1 }] }).catch(function (error) { offerError(error.message); });
          return;
        }

        var apply = target.closest(cartSelectors.codeApply);
        if (apply) {
          var field = drawer.querySelector(cartSelectors.codeInput);
          var entered = field ? field.value.trim().toUpperCase() : '';
          if (entered) {
            mutate(CU.routes.cartUpdate, { discount: codes(entered, null) })
              .then(function (data) { reportCode(data, entered); }).catch(function (error) { offerError(error.message); });
          }
          return;
        }

        var dropCode = target.closest(cartSelectors.codeRemove);
        if (dropCode) {
          mutate(CU.routes.cartUpdate, {
            discount: codes(null, dropCode.getAttribute('data-cu-cart-code-remove')),
          }).then(function () {
            var field = drawer.querySelector(cartSelectors.codeInput);
            if (field && drawer.getAttribute('aria-hidden') === 'false') field.focus({ preventScroll: true });
          }).catch(function (error) { offerError(error.message); });
        }
      }

      /* THE ONE PIECE OF CART TEXT THIS FILE AUTHORS.
         A bad discount code returns HTTP 200 with an unchanged cart, and Liquid
         cannot see it — `cart` has no discount_codes property, and
         discount_applications is empty precisely because nothing applied. So
         the rejection is only ever visible in this response, and the error
         element the section always renders is filled from here. The next
         repaint clears it, which is correct: any further change invalidates the
         complaint. See snippets/cu-cart-summary.liquid. */
      function reportCode(data, entered) {
        var slot = drawer.querySelector(cartSelectors.codeError);
        if (!slot || !data || !data.discount_codes) return;

        var rejected = data.discount_codes.some(function (entry) {
          return entry.code === entered && entry.applicable === false;
        });
        if (!rejected) return;

        slot.textContent = entered + ' isn’t a valid code for this cart.';
        slot.hidden = false;

        /* Put the code back so it can be corrected rather than retyped. */
        var field = drawer.querySelector(cartSelectors.codeInput);
        if (field) {
          field.value = entered;
          field.setAttribute('aria-invalid', 'true');
        }
      }

      /* The stepper is a <cu-qty> custom element and reports through change,
         so quantity is handled separately from the click delegation above. */
      function onDrawerChange(event) {
        if (adding || busyDepth > 0) return;
        var stepper = event.target.closest(cartSelectors.qty);
        if (!stepper) return;

        var qtyKey = keyFor(stepper);
        var field = stepper.querySelector('input');
        if (!qtyKey || !field) return;

        mutate(CU.routes.cartChange, { id: qtyKey, quantity: parseInt(field.value, 10) || 1 }).catch(function (error) { offerError(error.message); });
      }

      /* Enter in the code field applies it, rather than doing nothing or
         submitting the checkout form beneath. */
      function onDrawerKeydown(event) {
        if (event.key !== 'Enter') return;
        if (!event.target.closest(cartSelectors.codeInput)) return;
        event.preventDefault();
        var apply = drawer.querySelector(cartSelectors.codeApply);
        if (apply) apply.click();
      }

      drawer.addEventListener('click', onDrawerClick);
      drawer.addEventListener('change', onDrawerChange);
      drawer.addEventListener('keydown', onDrawerKeydown);

      var checkoutReady = false;
      function onCheckout(event) {
        if (!event.target.matches('[data-cu-cart-checkout]') || checkoutReady) return;
        event.preventDefault();
        if (adding || drawer.hasAttribute('data-cu-cart-busy')) return;
        if (window.CUCartSwap?.pending(snapshot()).length) {
          offerError('Finish or undo the pending pack switch before checking out.');
          return;
        }
        busy(true);
        // Re-read server-calculated totals with bundled HTML before leaving;
        // another tab may have changed the cart since this drawer was painted.
        mutate(CU.routes.cartUpdate, {}).then(async function () {
          var gifts = snapshot().items.filter(isGift);
          if (window.CUCartSwap?.pending(snapshot()).length) throw new Error('Finish or undo the pending pack switch before checking out.');
          var invalid = gifts.length > 1 || gifts.some(function (item) {
            return item.quantity !== 1 || item.final_line_price > 0 ||
              String(item.variant_id) !== offerSetting('gift-variant') ||
              Number(offerSetting('qualifying')) < Number(offerSetting('gift-threshold'));
          });
          if (invalid) throw new Error('Your gift could not be updated. Please try again before checking out.');
          // Optional recognition is bounded and fails open; discount validation above is unchanged.
          if (window.CUOrderMemory) await window.CUOrderMemory.prepare().catch(function () {});
          var form = drawer.querySelector('[data-cu-cart-checkout]');
          checkoutReady = true;
          form.requestSubmit(form.querySelector('[name="checkout"]'));
        }).catch(function (error) { offerError(error.message); })
          .finally(function () { checkoutReady = false; busy(false); });
      }
      drawer.addEventListener('submit', onCheckout);

      this.cuDrawerClick = onDrawerClick;
      this.cuDrawerChange = onDrawerChange;
      this.cuDrawerKeydown = onDrawerKeydown;
      this.cuCheckout = onCheckout;

      /* Re-render from the server without mutating anything. Used after an
         add-to-cart that happened elsewhere on the page, and as the recovery
         path when a mutation fails. */
      CU.refreshCart = function () {
        busy(true);
        /* Keep the HTML route's default content negotiation. Asking a product
           URL for application/json returns product data instead of sections. */
        var url = new URL(window.location.href);
        url.searchParams.set('sections', CART_SECTION);
        return fetch(url.pathname + url.search)
          .then(function (response) {
            if (!response.ok) throw new Error('Your bag could not refresh.');
            return response.json();
          })
          .then(function (sections) {
            if (!sections[CART_SECTION]) throw new Error('Your bag could not refresh.');
            paint(sections[CART_SECTION]);
            return CU.refreshCartCount();
          })
          .then(function (cart) {
            if (window.CUCartSwap?.pending(cart).length && CU.recoverCartPacks) {
              return CU.recoverCartPacks().then(function () { return CU.refreshCart(); });
            }
            return reconcileGift(cart).then(function () { return cart; });
          }).finally(function () { busy(false); });
      };

      /* The add response already includes the rendered drawer and its cart
         snapshot. Fill the open drawer without serial HTML/count fetches.
         A null section is recoverable: refresh, but never repeat the add. */
      CU.showAddedCart = function (sections) {
        var rendered = paint(sections && sections[CART_SECTION]);
        var cart;
        if (rendered) {
          try { cart = JSON.parse(content().getAttribute('data-cu-cart-state')); }
          catch (error) { cart = null; }
        }
        if (!cart || typeof cart.item_count !== 'number') {
          return CU.refreshCart();
        }
        paintCount(cart);
        // The item is confirmed by Shopify. Show it while the remaining
        // discount/gift work finishes; checkout stays disabled until settled.
        if (adding) {
          addConfirmed = true;
          setAddLoading(true);
          if (drawer.getAttribute('aria-hidden') === 'false') content().querySelector('[data-cu-cart-close]').focus({ preventScroll: true });
        }
        return reconcileGift();
      };

      // Prepare attribution while the page is being read so the first Add
      // normally still needs only its single bundled request.
      if (usesRebuyOffers()) ensureOffers().catch(function (error) { offerError(error.message); });
      if (usesDiscountPlus()) {
        initialCartWork = CU.refreshCart().finally(function () { initialCartWork = null; });
        initialCartWork.catch(function (error) { offerError(error.message); });
      }
    },

    onUnload: function () {
      document.removeEventListener('click', this.cuToggleHandler);
      document.removeEventListener('keydown', this.cuKeydown);
      this.container.removeEventListener('click', this.cuDrawerClick);
      this.container.removeEventListener('change', this.cuDrawerChange);
      this.container.removeEventListener('keydown', this.cuDrawerKeydown);
      this.container.removeEventListener('submit', this.cuCheckout);
    },
  };

  register('cu-cart', cartDrawer);

  /**
   * Refresh the bag count from the live cart. Used after add-to-cart until the
   * full Section Rendering pipeline exists.
   */
  CU.refreshCartCount = function () {
    return fetch(CU.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        CU.latestAjaxCart = cart;
        document.querySelectorAll(cartSelectors.count).forEach(function (el) {
          el.textContent = cart.item_count;
        });
        if (!document.querySelector('#cu-cart[data-cu-cart-busy], #cu-cart[data-cu-cart-adding]')) emit('theme:cart:change', { cart: cart });
        return cart;
      });
  };

  /* ================ Header — component 10 ==============================
     Registry module, not a custom element: it owns the whole section container
     and is never replaced by the Section Rendering API.

     The dropdown opens on hover ONLY where hover is real, and always on click
     and keyboard, so it is reachable on touch and without a pointer. The
     prototype duplicates markup behind breakpoint conditionals to get this;
     here it is one tree and one matchMedia.
     ==================================================================== */

  var headerSelectors = {
    item: '[data-cu-nav-item]',
    trigger: '[data-cu-nav-trigger]',
    panel: '[data-cu-nav-panel]',
    close: '[data-cu-nav-close]',
  };

  var header = {
    onLoad: function () {
      var container = this.container;
      var self = this;
      var canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      var desktop = window.matchMedia('(min-width: 1000px)');
      var backdrop = container.querySelector('[data-cu-nav-dismiss]');
      var productSection = document.querySelector('.cu-pdp, .cu-bundle');
      if (productSection) container.style.setProperty('--cu-accent', getComputedStyle(productSection).getPropertyValue('--cu-accent').trim());

      this.cuOpenItem = null;

      function panelFor(item) {
        return item.querySelector(headerSelectors.panel);
      }

      function triggerFor(item) {
        return item.querySelector(headerSelectors.trigger);
      }

      function open(item) {
        if (!desktop.matches) return;
        if (self.cuOpenItem === item) return;
        closeAll();
        var panel = panelFor(item);
        var trigger = triggerFor(item);
        if (!panel || !trigger) return;
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        self.cuOpenItem = item;
        container.setAttribute('data-menu-open', '');
        if (backdrop) backdrop.hidden = false;
      }

      function closeAll() {
        container.querySelectorAll(headerSelectors.item).forEach(function (item) {
          var panel = panelFor(item);
          var trigger = triggerFor(item);
          if (panel) panel.hidden = true;
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
        self.cuOpenItem = null;
        container.removeAttribute('data-menu-open');
        if (backdrop) backdrop.hidden = true;
      }

      this.cuCloseAll = closeAll;

      container.querySelectorAll(headerSelectors.item).forEach(function (item) {
        var trigger = triggerFor(item);

        // Pointer: open on enter, but only on a device that really hovers.
        item.addEventListener('mouseenter', function () {
          if (canHover.matches) {
            if (trigger) open(item);
            else closeAll();
          }
        });
        if (!trigger) return;

        // Click always works — and on touch the first tap opens rather than
        // navigating, which is the behaviour people expect from a menu parent.
        trigger.addEventListener('click', function (event) {
          event.preventDefault();
          if (self.cuOpenItem === item) {
            closeAll();
            return;
          }
          open(item);
          if (event.detail === 0) panelFor(item).querySelector('a[href]')?.focus();
        });
      });

      container.querySelectorAll(headerSelectors.close).forEach(function (el) {
        el.addEventListener('click', closeAll);
      });
      if (backdrop) {
        backdrop.addEventListener('click', closeAll);
        // Movement dismisses; appearing beneath a stationary pointer must not
        // immediately close a menu that was opened with the keyboard.
        backdrop.addEventListener('mousemove', function () { if (canHover.matches) closeAll(); });
      }

      this.cuLeave = function () { if (canHover.matches) closeAll(); };
      this.cuKeydown = function (event) {
        if (event.key === 'Escape' && self.cuOpenItem) {
          var trigger = triggerFor(self.cuOpenItem);
          closeAll();
          trigger.focus({ preventScroll: true });
        }
      };
      this.cuDocClick = function (event) {
        if (!container.contains(event.target) || event.target.closest('.cu-header__actions')) closeAll();
      };
      this.cuNavFocusOut = function (event) {
        if (!container.contains(event.relatedTarget)) closeAll();
      };
      this.cuNavResize = function () { if (!desktop.matches) closeAll(); };
      desktop.addEventListener('change', this.cuNavResize);
      this.cuNavResizeCleanup = function () { desktop.removeEventListener('change', self.cuNavResize); };

      container.addEventListener('mouseleave', this.cuLeave);
      container.addEventListener('focusout', this.cuNavFocusOut);
      document.addEventListener('keydown', this.cuKeydown);
      document.addEventListener('click', this.cuDocClick);

      // Keep the section's space in the page; only move the header itself.
      // The mobile drawer is its sibling and must stay fixed to the viewport.
      var previousY = Math.max(0, window.scrollY);
      var scrollFrame = 0;
      function revealHeader() { container.classList.remove('is-scroll-hidden'); }
      function updateHeader() {
        scrollFrame = 0;
        var currentY = Math.max(0, Math.min(window.scrollY, document.documentElement.scrollHeight - window.innerHeight));
        var delta = currentY - previousY;
        var promo = document.getElementById('shopify-section-cu-promo-bar');
        var topOffset = promo ? promo.offsetHeight : 0;
        var focused = container.contains(document.activeElement) && document.activeElement.matches(':focus-visible');
        var overlayOpen = document.querySelector('#cu-nav-drawer[aria-hidden="false"], #cu-cart[aria-hidden="false"]');
        if (currentY <= topOffset || focused || overlayOpen) {
          revealHeader();
        } else if (Math.abs(delta) < 4) {
          return;
        } else if (delta < 0) {
          revealHeader();
        } else if (currentY > topOffset + container.offsetHeight) {
          closeAll();
          container.classList.add('is-scroll-hidden');
        }
        previousY = currentY;
      }
      this.cuScroll = function () {
        if (!scrollFrame) scrollFrame = requestAnimationFrame(updateHeader);
      };
      this.cuFocus = revealHeader;
      this.cuScrollCleanup = function () { cancelAnimationFrame(scrollFrame); revealHeader(); };
      window.addEventListener('scroll', this.cuScroll, { passive: true });
      container.addEventListener('focusin', this.cuFocus);
    },

    onUnload: function () {
      this.container.removeEventListener('mouseleave', this.cuLeave);
      this.container.removeEventListener('focusout', this.cuNavFocusOut);
      this.cuNavResizeCleanup();
      document.removeEventListener('keydown', this.cuKeydown);
      document.removeEventListener('click', this.cuDocClick);
      window.removeEventListener('scroll', this.cuScroll);
      this.container.removeEventListener('focusin', this.cuFocus);
      this.cuScrollCleanup();
    },
  };

  register('cu-header', header);

  /* ================ Mobile drawer — component 11 =======================
     Lives outside .cu-page (rendered from cu-header, a sibling of <main>), so
     position:fixed resolves against the viewport. Inside .cu-page the
     container-type would make it scroll with the document.
     ==================================================================== */

  var drawerSelectors = {
    drawer: '[data-cu-drawer]',
    open: '[data-cu-drawer-open]',
    close: '[data-cu-drawer-close]',
  };

  function initNavDrawer() {
    var drawer = document.querySelector(drawerSelectors.drawer);
    if (!drawer || drawer.dataset.cuDrawerBound) return;
    drawer.dataset.cuDrawerBound = 'true';

    var panel = drawer.querySelector('.cu-drawer__panel');
    var returnFocus = null;
    var inertElements = [];
    var focusFrame = 0;

    function setOpen(open, restoreFocus) {
      if ((drawer.getAttribute('aria-hidden') === 'false') === open) return;
      if (open) {
        returnFocus = document.activeElement;
        // Only inert the branches outside this dialog, preserving existing state.
        var branch = drawer;
        while (branch.parentElement && branch !== document.body) {
          Array.from(branch.parentElement.children).forEach(function (sibling) {
            if (sibling !== branch && !sibling.inert) {
              sibling.inert = true;
              inertElements.push(sibling);
            }
          });
          branch = branch.parentElement;
        }
      } else {
        inertElements.forEach(function (element) { element.inert = false; });
        inertElements = [];
      }
      drawer.inert = !open;
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.querySelectorAll(drawerSelectors.open).forEach(function (el) {
        el.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
      emit(open ? 'theme:scroll:lock' : 'theme:scroll:unlock', open ? panel : undefined);
      cancelAnimationFrame(focusFrame);
      if (open) focusFrame = requestAnimationFrame(function () {
        drawer.querySelector('.cu-drawer__close').focus({ preventScroll: true });
      });
      else if (restoreFocus !== false && returnFocus && returnFocus.isConnected && returnFocus.getClientRects().length) returnFocus.focus({ preventScroll: true });
    }

    // Release the navigation lock before the shared bag click handler opens cart.
    drawer.addEventListener('click', function (event) {
      if (event.target.closest('[data-cu-cart-toggle]')) setOpen(false, false);
    }, true);

    document.addEventListener('click', function (event) {
      if (event.target.closest(drawerSelectors.open)) {
        event.preventDefault();
        setOpen(true);
        return;
      }
      if (event.target.closest(drawerSelectors.close)) {
        setOpen(false, !event.target.closest('a[href]'));
      }
    });

    document.addEventListener('keydown', function (event) {
      if (drawer.getAttribute('aria-hidden') !== 'false') return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false);
      } else if (event.key === 'Tab') {
        var focusable = Array.from(panel.querySelectorAll('a[href], button, summary, [tabindex="0"]')).filter(function (element) { return element.getClientRects().length && !element.disabled; });
        var first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    drawer.querySelectorAll('.cu-drawer__group').forEach(function (group) {
      group.addEventListener('toggle', function () {
        if (!group.open) return;
        drawer.querySelectorAll('.cu-drawer__group').forEach(function (other) { if (other !== group) other.open = false; });
      });
    });

    // Close if the viewport grows past the drawer breakpoint while it is open —
    // otherwise scroll stays locked behind an invisible panel.
    var wide = window.matchMedia('(min-width: 1000px)');
    var onChange = function (event) {
      if (event.matches && drawer.getAttribute('aria-hidden') === 'false') setOpen(false);
    };
    if (wide.addEventListener) wide.addEventListener('change', onChange);
    else if (wide.addListener) wide.addListener(onChange);
  }

  /* ================ <cu-qty> — primitive 3 =============================
     Custom element, not a registry module: cart lines get replaced wholesale
     by the Section Rendering API, and custom elements re-upgrade on insertion.
     ==================================================================== */

  var qtySelectors = {
    input: '[data-cu-qty-input]',
    up: '[data-cu-qty-up]',
    down: '[data-cu-qty-down]',
  };

  if (!window.customElements.get('cu-qty')) {
    window.customElements.define(
      'cu-qty',
      class CuQty extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector(qtySelectors.input);
          this.up = this.querySelector(qtySelectors.up);
          this.down = this.querySelector(qtySelectors.down);
          if (!this.input) return;

          this.min = parseInt(this.input.getAttribute('min'), 10) || 1;
          this.max = parseInt(this.input.getAttribute('max'), 10) || 9;
          this.line = this.getAttribute('data-cu-qty-line');

          this.onUp = this.step.bind(this, 1);
          this.onDown = this.step.bind(this, -1);
          this.onInput = this.commit.bind(this);

          if (this.up) this.up.addEventListener('click', this.onUp);
          if (this.down) this.down.addEventListener('click', this.onDown);
          this.input.addEventListener('change', this.onInput);

          this.reflect();
        }

        disconnectedCallback() {
          if (this.up) this.up.removeEventListener('click', this.onUp);
          if (this.down) this.down.removeEventListener('click', this.onDown);
          if (this.input) this.input.removeEventListener('change', this.onInput);
        }

        get value() {
          return parseInt(this.input.value, 10) || this.min;
        }

        step(delta) {
          var next = this.clamp(this.value + delta);
          if (next === this.value) return;
          this.input.value = next;
          this.commit();
        }

        clamp(n) {
          return Math.min(this.max, Math.max(this.min, n));
        }

        commit(event) {
          // Replace the input's change with one event from the stepper so
          // typing and +/- clicks reach delegated cart listeners exactly once.
          if (event) event.stopPropagation();
          var next = this.clamp(this.value);
          this.input.value = next;
          this.reflect();

          this.dispatchEvent(new Event('change', { bubbles: true }));
          emit('cu:qty:change', { value: next, line: this.line, el: this });
        }

        /* The minus greys to --cu-disabled at the minimum rather than
           disappearing, so the control keeps its shape. */
        reflect() {
          var v = this.value;
          if (this.down) {
            this.down.toggleAttribute('data-at-min', v <= this.min);
            this.down.setAttribute('aria-disabled', v <= this.min ? 'true' : 'false');
          }
          if (this.up) {
            this.up.toggleAttribute('data-at-max', v >= this.max);
            this.up.setAttribute('aria-disabled', v >= this.max ? 'true' : 'false');
          }
        }
      }
    );
  }

  /* ================ <cu-accordion> — primitive 4 =======================
     Native <details>/<summary> does all the work. This only backfills the
     `name` attribute's exclusive behaviour for browsers that predate it
     (Safari < 17.2, Firefox < 130). Never reimplement open/close here.
     ==================================================================== */

  var supportsExclusiveDetails = 'name' in HTMLDetailsElement.prototype;

  if (!window.customElements.get('cu-accordion')) {
    window.customElements.define(
      'cu-accordion',
      class CuAccordion extends HTMLElement {
        connectedCallback() {
          if (supportsExclusiveDetails) return;

          this.group = this.getAttribute('data-cu-acc-group');
          if (!this.group) return;

          this.details = this.querySelector('details');
          if (!this.details) return;

          this.onToggle = this.closeSiblings.bind(this);
          this.details.addEventListener('toggle', this.onToggle);
        }

        disconnectedCallback() {
          if (this.details && this.onToggle) {
            this.details.removeEventListener('toggle', this.onToggle);
          }
        }

        closeSiblings() {
          if (!this.details.open) return;
          var group = this.group;
          var mine = this.details;
          document
            .querySelectorAll('cu-accordion[data-cu-acc-group="' + group + '"] details')
            .forEach(function (el) {
              if (el !== mine) el.open = false;
            });
        }
      }
    );
  }

  /* ================ <cu-rail> — primitive 7 ============================
     Scroll behaviour is native: overflow-x + scroll-snap. This adds only the
     optional pagination dots and keyboard paging.

     No fade masks — the design tried them and removed them. Do not add them.
     ==================================================================== */

  var railSelectors = {
    rail: '[data-cu-rail]',
    dots: '[data-cu-rail-dots]',
  };

  if (!window.customElements.get('cu-rail')) {
    window.customElements.define(
      'cu-rail',
      class CuRail extends HTMLElement {
        connectedCallback() {
          this.rail = this.querySelector(railSelectors.rail);
          this.dotsHost = this.querySelector(railSelectors.dots);
          if (!this.rail) return;

          this.items = [].slice.call(this.rail.children);
          this.onScroll = this.schedule.bind(this);
          this.onKey = this.key.bind(this);

          this.rail.addEventListener('scroll', this.onScroll, { passive: true });
          this.rail.addEventListener('keydown', this.onKey);

          if (this.dotsHost && this.items.length > 1) this.buildDots();
          this.sync();
        }

        disconnectedCallback() {
          if (!this.rail) return;
          this.rail.removeEventListener('scroll', this.onScroll);
          this.rail.removeEventListener('keydown', this.onKey);
        }

        buildDots() {
          var self = this;
          this.dotsHost.innerHTML = '';
          this.dots = this.items.map(function (item, i) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'cu-rail__dot';
            dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
            dot.addEventListener('click', function () {
              self.rail.scrollTo({ left: item.offsetLeft - self.rail.offsetLeft, behavior: 'smooth' });
            });
            self.dotsHost.appendChild(dot);
            return dot;
          });
          this.dotsHost.removeAttribute('aria-hidden');
        }

        key(event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          var by = this.rail.clientWidth * 0.8;
          this.rail.scrollBy({
            left: event.key === 'ArrowRight' ? by : -by,
            behavior: 'smooth',
          });
        }

        schedule() {
          if (this.ticking) return;
          this.ticking = true;
          var self = this;
          window.requestAnimationFrame(function () {
            self.sync();
            self.ticking = false;
          });
        }

        /* Active item = the one whose left edge is nearest the scroll offset.
           Robust to variable-width items, which fixed-width maths is not. */
        sync() {
          if (!this.dots || !this.dots.length) return;
          var origin = this.rail.scrollLeft + this.rail.offsetLeft;
          var best = 0;
          var bestDist = Infinity;

          this.items.forEach(function (item, i) {
            var dist = Math.abs(item.offsetLeft - origin);
            if (dist < bestDist) {
              bestDist = dist;
              best = i;
            }
          });

          this.dots.forEach(function (dot, i) {
            dot.classList.toggle('is-active', i === best);
            dot.setAttribute('aria-current', i === best ? 'true' : 'false');
          });

          this.setAttribute('data-active-index', best);
        }
      }
    );
  }

  /* ================ <cu-gallery> — component 13 ========================
     One element, two layouts. Below 700 the rail scrolls and dots track it;
     at 700+ only the active slide is displayed and the thumbnails select it.
     Scroll and snap are native — this only tracks state.
     ==================================================================== */

  var gallerySelectors = {
    rail: '[data-cu-gallery-rail]',
    slide: '[data-cu-gallery-slide]',
    thumbs: '[data-cu-gallery-thumb]',
    dots: '[data-cu-gallery-dots]',
    zoom: '[data-cu-gallery-zoom]',
  };

  if (!window.customElements.get('cu-gallery')) {
    window.customElements.define(
      'cu-gallery',
      class CuGallery extends HTMLElement {
        connectedCallback() {
          this.rail = this.querySelector(gallerySelectors.rail);
          if (!this.rail) return;

          this.slides = [].slice.call(this.querySelectorAll(gallerySelectors.slide));
          this.thumbs = [].slice.call(this.querySelectorAll(gallerySelectors.thumbs));
          this.dotsHost = this.querySelector(gallerySelectors.dots);
          this.zoomBtn = this.querySelector(gallerySelectors.zoom);
          this.wide = window.matchMedia('(min-width: 700px)');
          this.index = 0;

          this.buildDots();

          var self = this;
          this.onScroll = function () {
            if (self.wide.matches || self.ticking) return;
            self.ticking = true;
            window.requestAnimationFrame(function () {
              self.syncFromScroll();
              self.ticking = false;
            });
          };
          this.rail.addEventListener('scroll', this.onScroll, { passive: true });

          this.thumbs.forEach(function (thumb, i) {
            thumb.addEventListener('click', function () { self.select(i); });
          });

          if (this.zoomBtn) {
            this.onZoom = this.zoom.bind(this);
            this.zoomBtn.addEventListener('click', this.onZoom);
          }

          this.select(0);
        }

        disconnectedCallback() {
          if (this.rail) this.rail.removeEventListener('scroll', this.onScroll);
          if (this.zoomBtn && this.onZoom) this.zoomBtn.removeEventListener('click', this.onZoom);
        }

        buildDots() {
          if (!this.dotsHost || this.slides.length < 2) return;
          var self = this;
          this.dotsHost.innerHTML = '';
          this.dots = this.slides.map(function (slide, i) {
            var dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'cu-gallery__dot';
            dot.setAttribute('aria-label', 'View image ' + (i + 1));
            dot.addEventListener('click', function () { self.select(i); });
            self.dotsHost.appendChild(dot);
            return dot;
          });
          this.dotsHost.removeAttribute('aria-hidden');
        }

        select(i) {
          this.index = i;

          // At 700+ only the active slide renders; below 700 the rail scrolls.
          this.slides.forEach(function (slide, n) {
            slide.classList.toggle('is-active', n === i);
          });

          if (!this.wide.matches && this.slides[i]) {
            this.rail.scrollTo({ left: this.slides[i].offsetLeft - this.rail.offsetLeft, behavior: 'smooth' });
          }

          this.reflect();
        }

        syncFromScroll() {
          var origin = this.rail.scrollLeft + this.rail.offsetLeft;
          var best = 0;
          var bestDist = Infinity;
          this.slides.forEach(function (slide, n) {
            var dist = Math.abs(slide.offsetLeft - origin);
            if (dist < bestDist) { bestDist = dist; best = n; }
          });
          if (best === this.index) return;
          this.index = best;
          this.reflect();
        }

        reflect() {
          var i = this.index;
          this.slides.forEach(function (slide, n) { slide.classList.toggle('is-active', n === i); });
          if (this.zoomBtn) this.zoomBtn.disabled = !this.slides[i]?.querySelector('img');
          if (this.dots) {
            this.dots.forEach(function (dot, n) { dot.classList.toggle('is-active', n === i); });
          }
          this.thumbs.forEach(function (thumb, n) { thumb.classList.toggle('is-active', n === i); });
        }

        /* Minimal viewer. PhotoSwipe is not loaded on this layout and pulling in
           41 KB for one button would undo the point of the isolation. */
        zoom() {
          var img = this.slides[this.index] && this.slides[this.index].querySelector('img');
          if (!img) return;

          var overlay = document.createElement('div');
          overlay.className = 'cu-zoom';
          overlay.innerHTML =
            '<button class="cu-zoom__close" type="button" aria-label="Close">&times;</button>' +
            '<img src="' + (img.currentSrc || img.src) + '" alt="' + (img.alt || '') + '">';

          function close() {
            overlay.remove();
            emit('theme:scroll:unlock');
            document.removeEventListener('keydown', onKey);
          }
          function onKey(e) { if (e.key === 'Escape') close(); }

          overlay.addEventListener('click', close);
          document.addEventListener('keydown', onKey);
          document.body.appendChild(overlay);
          emit('theme:scroll:lock', overlay);
        }
      }
    );
  }

  /* ================ Product page ======================================
     Registry module. Owns form submission, the pack<->sticky-bar link and the
     sticky bar's visibility.
     ==================================================================== */

  var pdpSelectors = {
    form: '[data-cu-buybox] form',
    packInput: '[data-cu-pack-input]',
    upsell: '[data-cu-upsell]',
    atc: '[data-cu-atc]',
    bar: '[data-cu-buybar]',
    barPanel: '[data-cu-buybar-packs]',
    barToggle: '[data-cu-buybar-toggle]',
    barPack: '[data-cu-buybar-pack]',
    barAdd: '[data-cu-buybar-add]',
    barQty: '[data-cu-buybar-qty]',
    barName: '[data-cu-buybar-name]',
    barUse: '[data-cu-buybar-use]',
    barPct: '[data-cu-buybar-pct]',
  };

  var pdp = {
    onLoad: function () {
      var container = this.container;
      var self = this;
      var form = container.querySelector(pdpSelectors.form);
      var bar = document.querySelector(pdpSelectors.bar);
      if (bar) {
        var productStyle = getComputedStyle(container);
        ['--cu-accent', '--cu-accent-tint'].forEach(function (name) { bar.style.setProperty(name, productStyle.getPropertyValue(name)); });
      }
      function updatePrice(source) {
        if (!form) return;
        var input = form.querySelector('[name="id"]:checked') || form.querySelector('input[type="hidden"][name="id"]');
        if (!input || !input.hasAttribute('data-price')) return;
        var qty = form.querySelector('[name="quantity"]');
        var barQty = bar && bar.querySelector('[data-cu-buybar-qty] input');
        if (source && source.closest('[data-cu-buybar-qty]') && barQty && qty) qty.value = barQty.value;
        else if (barQty && qty) barQty.value = qty.value;
        if (barQty) barQty.closest('cu-qty')?.reflect();
        if (qty) qty.closest('cu-qty')?.reflect();
        var price = Number(input.dataset.price) || 0;
        var upsell = form.querySelector(pdpSelectors.upsell);
        var total = price * (Number(qty && qty.value) || 1) + (upsell && upsell.checked ? Number(upsell.dataset.price) || 0 : 0);
        function money(cents) { return 'RM' + (cents / 100).toFixed(cents % 100 ? 2 : 0); }
        var saving = form.querySelector('[data-cu-pack-saving]');
        if (saving) {
          var saved = Math.max(0, (Number(input.dataset.comparePrice) || 0) - price);
          saving.textContent = 'YOU SAVE ' + money(saved); saving.hidden = !saved;
        }
        [form.querySelector('[data-cu-price-label]'), bar && bar.querySelector(pdpSelectors.barAdd)].forEach(function (button) {
          if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
          var label = button.querySelector('.cu-btn__label');
          if (label) label.textContent = (button.dataset.cuPriceLabel || 'Add to cart') + ' · ' + money(total);
        });
      }
      this.cuPriceChange = function (event) { updatePrice(event.target); };
      if (form) form.addEventListener('change', this.cuPriceChange);
      if (bar) bar.addEventListener('change', this.cuPriceChange);
      updatePrice();

      /* --- Add to cart -------------------------------------------------
         Intercepted because the bundle upsell adds a SECOND variant, and a
         Shopify product form can only post one. Posting items[] to
         /cart/add.js handles both. Without JS the native submit still adds the
         main product — the right way round to degrade. */
      if (form) {
        this.cuSubmit = function (event) {
          event.preventDefault();

          var data = new FormData(form);
          var items = [{ id: data.get('id'), quantity: parseInt(data.get('quantity'), 10) || 1 }];

          var upsell = container.querySelector(pdpSelectors.upsell);
          if (upsell && upsell.checked && upsell.value) {
            items.push({ id: upsell.value, quantity: 1 });
          }

          addItems(form, items, bar && bar.querySelector(pdpSelectors.barAdd));
        };

        form.addEventListener('submit', this.cuSubmit);
      }

      /* --- Pack selection mirrors into the sticky bar ------------------
         The radios stay the single source of truth; the bar only reflects
         them, so the two can never disagree. */
      function syncBar(input) {
        if (!bar || !input) return;
        var row = bar.querySelector('[data-cu-buybar-pack="' + input.value + '"]');

        bar.querySelectorAll(pdpSelectors.barPack).forEach(function (btn) {
          btn.setAttribute('aria-pressed', btn === row ? 'true' : 'false');
        });
        if (!row) return;

        /* The summary is filled from the bar's OWN row for that variant, not
           re-derived from the buy box. Every string there was rendered by
           Liquid from the same variant, so the two cannot drift, and the
           name never picks up the tag text the way reading .cu-pack__name
           would. */
        function rowText(sel) {
          var el = row.querySelector(sel);
          return el ? el.textContent.trim() : '';
        }

        var barName = bar.querySelector(pdpSelectors.barName);
        var barUse = bar.querySelector(pdpSelectors.barUse);
        var barPct = bar.querySelector(pdpSelectors.barPct);

        if (barName) barName.textContent = rowText('.cu-buybar__row-name');
        if (barUse) barUse.textContent = rowText('.cu-buybar__row-use');
        if (barPct) {
          var pct = parseInt(row.getAttribute('data-pct'), 10) || 0;
          barPct.textContent = '-' + pct + '%';
          barPct.toggleAttribute('hidden', pct <= 0);
        }
      }

      container.querySelectorAll(pdpSelectors.packInput).forEach(function (input) {
        input.addEventListener('change', function () {
          syncBar(input);
          updatePrice();
          emit('cu:variant:change', { id: input.value });
        });
      });

      if (bar) {
        bar.querySelectorAll(pdpSelectors.barPack).forEach(function (btn) {
          btn.addEventListener('click', function () {
            var id = btn.getAttribute('data-cu-buybar-pack');
            var input = container.querySelector('[data-cu-pack-input][value="' + id + '"]');
            if (!input) return;
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
            togglePanel(false);
          });
        });

        /* Astra uses a native modal sheet. Move the existing purchase controls
           into its footer so quantity, loading and cart state stay shared. */
        var panel = bar.querySelector(pdpSelectors.barPanel);
        var dialog = bar.querySelector('[data-cu-buybar-dialog]');
        var actions = bar.querySelector('.cu-buybar__actions');
        var sheetFooter = bar.querySelector('[data-cu-buybar-sheet-footer]');
        var sheetLocked = false;
        function togglePanel(open, pointerOpened) {
          if (!panel) return;
          var next = open === undefined ? !panel.hasAttribute('data-open') : open;
          if (next === panel.hasAttribute('data-open')) return;
          var toggle = panel.querySelector(pdpSelectors.barToggle);
          if (dialog) {
            panel.toggleAttribute('data-open', next);
            if (toggle) toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
            if (next) {
              // Safari can mark programmatic modal focus as focus-visible even
              // after a tap. Keep focus in the sheet without a second touch ring.
              dialog.toggleAttribute('data-cu-pointer-focus', pointerOpened === true);
              sheetFooter.appendChild(actions);
              lockScroll();
              sheetLocked = true;
              dialog.showModal();
              var selected = dialog.querySelector('[aria-pressed="true"]');
              if (selected) selected.focus({ preventScroll: true });
            } else {
              if (dialog.open) dialog.close();
              bar.appendChild(actions);
              if (sheetLocked) {
                sheetLocked = false;
                unlockScroll();
              }
              if (toggle && !bar.hidden && window.matchMedia('(max-width: 699.98px)').matches) {
                toggle.focus({ preventScroll: true });
              }
            }
            return;
          }
          if (!next && panel.querySelector('.cu-buybar__list').contains(document.activeElement) && toggle) {
            toggle.focus({ preventScroll: true });
          }
          panel.toggleAttribute('data-open', next);
          if (toggle) toggle.setAttribute('aria-expanded', next ? 'true' : 'false');
        }
        this.cuCloseBuybar = function () { togglePanel(false); };

        if (dialog) {
          this.cuBuybarPointer = function () { dialog.setAttribute('data-cu-pointer-focus', ''); };
          this.cuBuybarKeyboard = function (event) {
            if (!['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) dialog.removeAttribute('data-cu-pointer-focus');
          };
          this.cuBuybarCancel = function (event) { event.preventDefault(); togglePanel(false); };
          this.cuBuybarDialogClose = function () { if (!dialog.open) togglePanel(false); };
          this.cuBuybarBackdrop = function (event) {
            if (event.target !== dialog) return;
            var rect = dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
              togglePanel(false);
            }
          };
          dialog.addEventListener('cancel', this.cuBuybarCancel);
          dialog.addEventListener('close', this.cuBuybarDialogClose);
          dialog.addEventListener('click', this.cuBuybarBackdrop);
          dialog.addEventListener('pointerdown', this.cuBuybarPointer);
          dialog.addEventListener('keydown', this.cuBuybarKeyboard);
        }

        bar.querySelectorAll(pdpSelectors.barToggle).forEach(function (btn) {
          btn.addEventListener('click', function (event) { togglePanel(undefined, event.detail > 0); });
        });

        if (panel) {
          panel.querySelector('[data-cu-buybar-close]').addEventListener('click', function () { togglePanel(false); });
          this.cuPanelOutside = function (event) {
            if (dialog) return; // A backdrop click closes only after pointer-up, preventing click-through.
            if (panel.hasAttribute('data-open') && !panel.contains(event.target)) togglePanel(false);
          };
          this.cuPanelEscape = function (event) {
            if (!dialog && event.key === 'Escape' && panel.hasAttribute('data-open')) {
              togglePanel(false);
              panel.querySelector(pdpSelectors.barToggle).focus({ preventScroll: true });
            }
          };
          document.addEventListener('pointerdown', this.cuPanelOutside);
          document.addEventListener('keydown', this.cuPanelEscape);
        }

        var add = bar.querySelector(pdpSelectors.barAdd);
        if (add && form) {
          add.addEventListener('click', function () {
            togglePanel(false);
            /* The bar has its own stepper, so push its value into the form
               before submitting — otherwise the bar would silently always
               add the quantity showing in the buy box. */
            var barQty = bar.querySelector(pdpSelectors.barQty);
            var formQty = form.querySelector('[name="quantity"]');
            if (barQty && formQty) {
              var field = barQty.querySelector('input');
              if (field) formQty.value = field.value;
            }
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          });
        }

        /* Reveal only after the entire inline CTA passes above the viewport.
           A CTA still below the screen must not show the bar at page load.
           CSS handles the below-700 width limit. */
        var anchor = container.querySelector(pdpSelectors.atc);
        var footer = document.querySelector('.cu-footer');
        function inView(element, bottomInset) {
          if (!element) return false;
          var rect = element.getBoundingClientRect();
          return rect.bottom > 0 && rect.top < window.innerHeight - bottomInset;
        }
        var footerVisible = inView(footer, 0);
        function updateBarVisibility() {
          var desktopSheet = dialog && window.matchMedia('(min-width: 700px)').matches;
          /* Safari can report transient page geometry while the modal locks
             the body or browser chrome resizes. Its locked background cannot
             scroll to the CTA/footer; keep the open sheet until dismissal. */
          if (dialog && dialog.open && !desktopSheet) return;
          var anchorPassed = anchor && anchor.getBoundingClientRect().bottom <= 0;
          var hidden = !anchorPassed || footerVisible || desktopSheet;
          if (hidden) togglePanel(false);
          bar.toggleAttribute('hidden', hidden);
        }
        updateBarVisibility();
        if ('IntersectionObserver' in window) {
          if (anchor) {
            this.cuObserver = new IntersectionObserver(updateBarVisibility);
            this.cuObserver.observe(anchor);
          }
          if (footer) {
            this.cuFooterObserver = new IntersectionObserver(function (entries) {
              footerVisible = entries[0].isIntersecting;
              updateBarVisibility();
            });
            this.cuFooterObserver.observe(footer);
          }
        }
        /* Also handle jumps that skip the CTA entirely: both observer samples
           can be non-intersecting even though the CTA changed sides. */
        this.cuBarScroll = function () {
          footerVisible = inView(footer, 0);
          updateBarVisibility();
        };
        window.addEventListener('scroll', this.cuBarScroll, { passive: true });
        window.addEventListener('resize', this.cuBarScroll);
      }
    },

    onUnload: function () {
      if (this.cuCloseBuybar) this.cuCloseBuybar();
      var form = this.container.querySelector(pdpSelectors.form);
      if (form && this.cuSubmit) form.removeEventListener('submit', this.cuSubmit);
      if (form && this.cuPriceChange) form.removeEventListener('change', this.cuPriceChange);
      var bar = document.querySelector(pdpSelectors.bar);
      var dialog = bar && bar.querySelector('[data-cu-buybar-dialog]');
      if (dialog) {
        dialog.removeEventListener('cancel', this.cuBuybarCancel);
        dialog.removeEventListener('close', this.cuBuybarDialogClose);
        dialog.removeEventListener('click', this.cuBuybarBackdrop);
        dialog.removeEventListener('pointerdown', this.cuBuybarPointer);
        dialog.removeEventListener('keydown', this.cuBuybarKeyboard);
      }
      if (bar && this.cuPriceChange) bar.removeEventListener('change', this.cuPriceChange);
      if (this.cuObserver) this.cuObserver.disconnect();
      if (this.cuFooterObserver) this.cuFooterObserver.disconnect();
      if (this.cuPanelOutside) document.removeEventListener('pointerdown', this.cuPanelOutside);
      if (this.cuPanelEscape) document.removeEventListener('keydown', this.cuPanelEscape);
      if (this.cuBarScroll) {
        window.removeEventListener('scroll', this.cuBarScroll);
        window.removeEventListener('resize', this.cuBarScroll);
      }
    },
  };

  register('cu-pdp', pdp);

  /* The kit page has the same three moving parts — a product form whose submit
     must be intercepted, a sticky bar with its own stepper, and the observer
     that reveals the bar once the inline CTA scrolls away. Every pack-specific
     branch above no-ops on a single-variant product, so the module is
     registered against both section types rather than copied. */
  register('cu-bundle', pdp);

  /* ================ Shop all ==========================================
     Narrows an already-rendered grid in the browser. Liquid cannot read a query
     string, so this never round-trips to the server and there is no URL state
     to restore — see the note at the top of sections/cu-shop.liquid.

     Counts are recomputed from what is actually visible rather than read from
     the chip labels, so a card the merchant later hides cannot leave the
     heading lying.
     ==================================================================== */

  var shopSelectors = {
    chip: '[data-cu-shop-filter]',
    card: '[data-cu-shop-item]',
    count: '[data-cu-shop-count]',
    none: '[data-cu-shop-none]',
  };

  var shop = {
    onLoad: function () {
      var container = this.container;
      var chips = container.querySelectorAll(shopSelectors.chip);
      var cards = container.querySelectorAll(shopSelectors.card);
      var count = container.querySelector(shopSelectors.count);
      var none = container.querySelector(shopSelectors.none);

      function apply(kind) {
        var shown = 0;

        cards.forEach(function (card) {
          var match = kind === 'all' || card.getAttribute('data-cu-shop-item') === kind;
          card.toggleAttribute('hidden', !match);
          if (match) shown += 1;
        });

        /* The chips are the primitive's selectable family, so the selected
           state is the same class and the same aria-pressed Liquid renders.
           One of them is always on — this is a radio group wearing pills. */
        chips.forEach(function (chip) {
          var on = chip.getAttribute('data-cu-shop-filter') === kind;
          chip.classList.toggle('is-selected', on);
          chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        if (count) count.textContent = shown + (shown === 1 ? ' PRODUCT' : ' PRODUCTS');
        if (none) none.toggleAttribute('hidden', shown > 0);
      }

      this.cuChips = [];
      chips.forEach(function (chip) {
        var handler = function () { apply(chip.getAttribute('data-cu-shop-filter')); };
        chip.addEventListener('click', handler);
        this.cuChips.push({ el: chip, handler: handler });
      }, this);
    },

    onUnload: function () {
      (this.cuChips || []).forEach(function (bound) {
        bound.el.removeEventListener('click', bound.handler);
      });
      this.cuChips = [];
    },
  };

  register('cu-shop', shop);

  /* ================ Boot =============================================== */

  /* Shared by product forms and card forms. Never resubmit after a failed
     refresh: the add may already have succeeded, and a second POST adds twice. */
  function addItems(form, items, linkedButton, options) {
    if (form.getAttribute('aria-busy') === 'true') return;
    if (!CU.beginCartAdd()) return;
    emit('theme:cart:add-start', { cartOffer: !!(options && options.cartOffer) });
    var buttons = Array.from(form.querySelectorAll('[type="submit"]'));
    if (linkedButton) buttons.push(linkedButton);
    var previous = buttons.map(function (button) {
      return { button: button, disabled: button.disabled, label: button.getAttribute('aria-label') };
    });
    var error = form.querySelector('[data-cu-add-error]');
    var added = false;
    var confirmed = false;
    var addBaseline, submittedItems, submissionStarted = false;
    form.setAttribute('aria-busy', 'true');
    buttons.forEach(function (button) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-label', 'Adding to bag, please wait');
      button.disabled = true;
    });
    if (error) error.hidden = true;

    return Promise.resolve().then(function () {
      if (CU.waitForCartReady) return CU.waitForCartReady();
    }).then(function () {
      if (options && options.validate) return options.validate();
    }).then(function () { return CU.prepareCartAdd(items); }).then(function (preparedItems) {
      if (CU.usesDiscountPlus && CU.usesDiscountPlus()) addBaseline = CU.latestAjaxCart;
      if (!options || !options.discountCode) return preparedItems;
      if (!CU.prepareProductOffer) throw new Error('This product offer is unavailable. Please reload and try again.');
      return CU.prepareProductOffer(options.discountCode).then(function () { return preparedItems; });
    }).then(function (preparedItems) {
      submittedItems = preparedItems; submissionStarted = true;
      if (CU.submitCartItems) return CU.submitCartItems(preparedItems);
      return fetch(CU.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: preparedItems, sections: CU.liveCart ? undefined : CART_SECTION, sections_url: CU.liveCart ? undefined : window.location.pathname }),
    }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || 'This item could not be added to your bag.');
          return data;
        });
      }); })
      .then(function (data) {
        added = true;
        if (options && options.onAdded) options.onAdded();
        return CU.showAddedCart(data.sections);
      })
      .then(function () { confirmed = true; })
      .catch(function (failure) {
        // A lost response can follow a committed add. Recover from Shopify's
        // cart once, never repeat the add, and keep the accepted offer pending
        // until that authoritative read has reconciled its discount.
        if (submissionStarted && addBaseline && CU.usesDiscountPlus && CU.usesDiscountPlus()) {
          return CU.refreshCart().then(function () {
            if (window.CUDiscountEngine.additionPresent(addBaseline, CU.latestAjaxCart, submittedItems)) {
              var alreadyAcknowledged = added;
              added = true; confirmed = true;
              if (!alreadyAcknowledged && options && options.onAdded) options.onAdded();
              return;
            }
            showAddFailure(failure);
          }).catch(function () { showAddFailure(new Error('Please reload your bag to check whether the item was added before trying again.')); });
        }
        showAddFailure(failure);
      })
      .finally(function () {
        if (CU.clearPendingProductOffer) CU.clearPendingProductOffer();
        form.removeAttribute('aria-busy');
        previous.forEach(function (state) {
          state.button.removeAttribute('aria-busy');
          state.button.disabled = state.disabled;
          if (state.label === null) state.button.removeAttribute('aria-label');
          else state.button.setAttribute('aria-label', state.label);
        });
        CU.finishCartAdd(confirmed || !!(options && options.keepCartOpen));
      });
      function showAddFailure(failure) {
        if (options && options.keepCartOpen && CU.cartOfferError) {
          CU.cartOfferError(added ? 'Added to your bag, but the bag could not refresh. Reload the page to view it.' : failure.message);
        }
        if (!error) return;
        error.textContent = added
          ? 'Added to your bag, but the bag could not refresh. Reload the page to view it.'
          : failure.message || 'Your bag could not be updated. Please try again.';
        error.hidden = false;
      }
  }

  // The reels viewer uses the same add pipeline as product and card forms.
  CU.addItems = addItems;

  function init() {
    load('*');
    initNavDrawer();
    bindDesignMode();
    bindScroll();
    bindResize();
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-cu-card-form]');
      if (!form) return;
      event.preventDefault();
      var data = new FormData(form);
      addItems(form, [{ id: data.get('id'), quantity: 1 }]);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
